import { useQuery } from '@tanstack/react-query';
import { formatPriceCents } from '@gobingebox/shared';
import { Ban, CreditCard, Inbox, MessageSquare, TrendingUp, Users, Wallet } from 'lucide-react';
import { Loader2 } from '@/components/icons';
import { adminApi } from './api';

function Stat({
  icon: Icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: typeof Wallet;
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 text-ink-muted">
        <Icon size={16} strokeWidth={1.75} className={accent ? 'text-brand-soft' : undefined} />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className={`mt-2 font-display text-2xl font-extrabold ${accent ? 'text-gradient' : 'text-ink'}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-ink-faint">{sub}</p>}
    </div>
  );
}

export function Overview() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-overview'], queryFn: adminApi.overview });

  if (isLoading || !data) {
    return (
      <p className="flex items-center gap-2 text-sm text-ink-faint">
        <Loader2 size={16} className="animate-spin" /> Loading dashboard…
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-ink-muted">Revenue</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat icon={Wallet} label="Net revenue" value={formatPriceCents(data.revenue.netCents)} sub={`${data.revenue.payments} payments all-time`} accent />
          <Stat icon={TrendingUp} label="This month" value={formatPriceCents(data.revenue.monthNetCents)} sub="net of refunds" />
          <Stat icon={CreditCard} label="Refunded" value={formatPriceCents(data.revenue.refundedCents)} sub={`gross ${formatPriceCents(data.revenue.grossCents)}`} />
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-ink-muted">Members &amp; plans</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Members" value={String(data.users.total)} sub={`+${data.users.new30d} in 30 days`} />
          <Stat icon={CreditCard} label="Active subscribers" value={String(data.subscriptions.active)} sub={`${data.subscriptions.paying} paying · ${data.subscriptions.comp} comp`} />
          <Stat icon={MessageSquare} label="Comments" value={String(data.comments.total)} sub={data.comments.held > 0 ? `${data.comments.held} awaiting review` : 'none held'} />
          <Stat icon={Inbox} label="Open submissions" value={String(data.openSubmissions)} />
          <Stat icon={Ban} label="Banned" value={String(data.users.banned)} />
        </div>
      </div>

      <p className="text-xs text-ink-faint">
        Revenue reflects logged Stripe payments (test mode shows test purchases). Refund individual
        payments under Payments.
      </p>
    </div>
  );
}
